import type { ReactNode } from "react";

export const formatDateTime = (value?: string | null) => {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const formatTime = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleTimeString("en-AU", { hour12: false });
};

export const formatDuration = (milliseconds?: number | null) => {
  if (milliseconds == null) return "-";
  if (milliseconds < 1000) return `${milliseconds}ms`;
  return `${(milliseconds / 1000).toFixed(1)}s`;
};

/** Scheduled runs are the integration acting on its own; manual runs name the person. */
export const triggeredBy = (run: { trigger: string; triggered_by: string | null }) =>
  run.trigger === "schedule" ? "Xero-API" : run.triggered_by || "Unknown user";

/**
 * Status reads as coloured text rather than a filled pill: a table of pills is
 * louder than the data it describes. Colour carries the exception, so anything
 * still in progress is deliberately plain.
 */
const statusColours: Record<string, string> = {
  Success: "text-emerald-600",
  Failed: "text-red-600",
  Cancelled: "text-amber-600",
};

const statusLabels: Record<string, string> = {
  queued: "Queued",
  running: "Running",
};

export const StatusBadge = ({ status }: { status: string }) => {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm font-medium ${
        statusColours[status] || "text-muted-foreground"
      }`}
    >
      {status === "running" && <Spinner />}
      {statusLabels[status] || status}
      {status === "queued" && (
        <span className="sr-only">, waiting for the run in front to finish</span>
      )}
    </span>
  );
};

/** A quiet indicator that work is genuinely under way, not merely waiting. */
export const Spinner = ({ className = "" }: { className?: string }) => (
  <span
    aria-hidden="true"
    className={`inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
  />
);

const levelColours: Record<string, string> = {
  error: "text-red-600",
  warn: "text-amber-600",
};

export const LevelBadge = ({ level }: { level: string }) => (
  <span className={`text-sm font-medium ${levelColours[level] || "text-muted-foreground"}`}>
    {level}
  </span>
);

export const Stat = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-1 text-sm">{children}</dd>
  </div>
);

/**
 * Xero reports employment status in caps as ACTIVE or TERMINATED. The portal
 * shows Active and Disabled so the wording matches the users screen, while the
 * raw Xero value stays stored as the source of truth.
 *
 * An unrecognised status is title-cased rather than hidden, so a new Xero value
 * still reads sensibly instead of disappearing.
 */
export function employeeStatusLabel(status?: string | null): string {
  const value = String(status || "").trim();
  if (!value) return "Unknown";

  const known: Record<string, string> = {
    ACTIVE: "Active",
    TERMINATED: "Disabled",
  };

  return (
    known[value.toUpperCase()] ||
    value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
  );
}
